import React, { useEffect, useState } from "react";
import Select from "react-select";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import { useTranslation } from "react-i18next";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import SelectInput from "../../components/forms/SelectInput";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";

export default function AdminRequestWalletTopUp() {
  const { t } = useTranslation();

  const [selectedUser, setSelectedUser] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [availableWallets, setAvailableWallets] = useState([]);
  const [availableQuotes, setAvailableQuotes] = useState([]);
  const [walletId, setWalletId] = useState("");
  const [quoteId, setQuoteId] = useState("");
  const [amount, setAmount] = useState("");
  const [nTickets, setTicketCount] = useState("");
  const [message, setMessage] = useState("");
  const [lastAction, setLastAction] = useState("");

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchAllUsers();
  }, []);

  const fetchAllUsers = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/payers`);
      const uniqueUsersMap = {};
      res.data.forEach((row) => {
        if (!uniqueUsersMap[row.user_id]) {
          uniqueUsersMap[row.user_id] = row;
        }
      });
      setAllUsers(Object.values(uniqueUsersMap));
    } catch {
      console.error("❌ Failed to fetch payers");
    }
  };

  const fetchAvailableWallets = async (userId) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/all-wallets-of/${userId}`);
      setAvailableWallets(res.data);
    } catch (err) {
      console.error("Failed to fetch wallets for user", err);
    }
  };

  const fetchQuotesForWallet = async (walletId) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/quotes-by-wallet/${walletId}`);
      setAvailableQuotes(res.data);
    } catch (err) {
      console.error("Failed to fetch quotes", err);
      setAvailableQuotes([]);
    }
  };

  const handleWalletTopup = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/admin/request-wallet-top-up`, {
        from_wallet_id: Number(walletId),
        delta_tickets_from_wallet: 0,
        delta_money_from_wallet: Number(amount),
        to_wallet_id: Number(walletId),
        delta_tickets_to_wallet: Number(nTickets),
        delta_money_to_wallet: 0,
        header: "request-wallet-top-up",
      });

      setMessage(`${t("admin_topup_request.messages.success")}`);
    } catch (err) {
      console.error(err);
      setMessage(`${t("admin_topup_request.messages.error")}`);
    }
  };

  const selectedUserOption = selectedUser
    ? {
        value: selectedUser,
        label:
          allUsers.find((u) => u.user_id === selectedUser)?.fullname +
          " | " +
          allUsers.find((u) => u.user_id === selectedUser)?.userno +
          " | " +
          allUsers.find((u) => u.user_id === selectedUser)?.sponsor,
      }
    : null;

  return (
    <StandardScreen
      background="default"
      topNav={<TopNav title={t("admin_topup_request.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={{ maxWidth: 720, width: "100%", margin: "0 auto" }}>
          <Stack gap="4">
            <Card>
              <Stack gap="4">
                <h1
                  style={{
                    margin: 0,
                    fontSize: "1.5rem",
                    fontWeight: 700,
                  }}
                >
                  {t("admin_topup_request.title")}
                </h1>

                <FormField label={t("admin_topup_request.fields.select_payer.label")}>
                  <div>
                    <Select
                      classNamePrefix="react-select"
                      options={allUsers.map((user) => ({
                        value: user.user_id,
                        label: `${user.fullname} | ${user.userno} | ${user.sponsor}`,
                      }))}
                      value={selectedUserOption}
                      onChange={(option) => {
                        const userId = option?.value || "";
                        setSelectedUser(userId);
                        setAvailableWallets([]);
                        setAvailableQuotes([]);
                        setWalletId("");
                        setQuoteId("");
                        setAmount("");
                        setTicketCount("");
                        setMessage("");
                        setLastAction("");

                        if (userId) fetchAvailableWallets(userId);
                      }}
                      placeholder={t("admin_topup_request.fields.select_payer.placeholder")}
                    />
                  </div>
                </FormField>

                {selectedUser && (
                  <FormField label={t("admin_topup_request.fields.select_wallet.label")}>
                    <SelectInput
                      value={walletId}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        setWalletId(selectedId);
                        setQuoteId("");
                        setAmount("");
                        setTicketCount("");
                        setLastAction("");
                        setMessage("");
                        setAvailableQuotes([]);

                        if (selectedId) fetchQuotesForWallet(selectedId);
                      }}
                    >
                      <option value="">
                        {t("admin_topup_request.fields.select_wallet.placeholder")}
                      </option>

                        {availableWallets.map((w) => (
                          <option key={w.wallet_id} value={w.wallet_id}>
                            {[
                              w.walletname,
                              w.route_name,
                              w.start_point && w.end_point ? `${w.start_point} → ${w.end_point}` : null,
                              w.bus_code ? `${t("admin_topup_request.fields.select_wallet.bus")}: ${w.bus_code}` : null,
                              w.plate_number ? `${t("admin_topup_request.fields.select_wallet.plate")}: ${w.plate_number}` : null,
                            ]
                              .filter(Boolean)
                              .join(" | ")}
                          </option>
                        ))}


                    </SelectInput>
                  </FormField>
                )}

                {walletId && (
                  <FormField label={t("admin_topup_request.fields.select_quote.label")}>
                    <SelectInput
                      value={quoteId}
                      onChange={(e) => {
                        const selected = availableQuotes.find(
                          (q) => q.quote_id === Number(e.target.value)
                        );
                        setQuoteId(e.target.value);

                        if (selected) {
                          setAmount(selected.price_after_tax);
                          setTicketCount(selected.batch);
                        }

                        setLastAction("");
                        setMessage("");
                      }}
                      disabled={!walletId || availableQuotes.length === 0}
                    >
                      <option value="">
                        {t("admin_topup_request.fields.select_quote.placeholder")}
                      </option>
                      {availableQuotes.map((q) => (
                        <option key={q.quote_id} value={q.quote_id}>
                          {`${q.batch} ${t("admin_topup_request.fields.tickets.unit")} | ${q.price_after_tax} ${t("admin_topup_request.fields.currency.tl")}`}
                        </option>
                      ))}
                    </SelectInput>
                  </FormField>
                )}

                {walletId && (
                  <>
                    <FormField label={t("admin_topup_request.fields.amount.label")}>
                      <TextInput
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                      />
                    </FormField>

                    <FormField label={t("admin_topup_request.fields.tickets.label")}>
                      <TextInput
                        type="number"
                        value={nTickets}
                        onChange={(e) => setTicketCount(e.target.value)}
                      />
                    </FormField>
                  </>
                )}

                {walletId && (
                  <Button
                    onClick={() => {
                      setLastAction("topup");
                      handleWalletTopup();
                    }}
                    disabled={
                      !amount ||
                      !nTickets ||
                      (lastAction === "topup" &&
                        (message.startsWith("✅") || message.startsWith("❌")))
                    }
                    fullWidth
                  >
                    {lastAction === "topup"
                      ? message.startsWith("✅")
                        ? t("admin_topup_request.status.success_short")
                        : message.startsWith("❌")
                        ? `${t("admin_topup_request.status.error_short")}: ${message.replace("❌ ", "")}`
                        : t("admin_topup_request.actions.submit")
                      : t("admin_topup_request.actions.submit")}
                  </Button>
                )}
              </Stack>
            </Card>

            <StatusMessage message={message} />
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}